// When a practice continues, pauses or ends, and whether it counts. Pure functions.

import { findMove, lookup, shareOf, sparringReplies } from "./catalogTree.ts";
import type { Tree } from "./catalogTree.ts";

export const END_REASON = {
    LINE_COMPLETE: "line_complete",   // the book of this line has nothing deeper
    MAX_MOVES: "max_moves",
    OFF_BOOK: "off_book",             // the user left the book and chose to stop there
    GAME_OVER: "game_over",
    ABANDONED: "abandoned",
} as const;

export type EndReason = (typeof END_REASON)[keyof typeof END_REASON];

/** Why the practice stopped moving, or that it did not. */
export type NextStep =
    | { next: "reply" }
    | { next: "end"; reason: EndReason }
    | { next: "offbook"; rarity: "rare" | "unknown" };

/**
 * A practice is fifteen moves of the user's (ten was too few for a Sicilian, 2026-11-01), and goes on past that for
 * as long as the user keeps playing their repertoire's move: studying the opening is what the repertoire is for.
 * Forty is where it stops whatever happens.
 */
export const DEFAULT_MAX_OWN_MOVES = 15;
export const REPERTOIRE_MAX_OWN_MOVES = 40;
export const MIN_MOVES_TO_COUNT = 3;
const RARE_MOVE_SHARE = 0.04;   // below this the generator does not follow a move

/**
 * What happens after the user's move.
 *   reply: the sparring partner answers; end: the practice is over;
 *   offbook: the move left the book, the user decides whether to take it back or stop.
 */
export function afterUserMove({ tree, fenBefore, fenAfter, uci, ownMoves,
                                maxOwnMoves = DEFAULT_MAX_OWN_MOVES, gameOver = false, repertoireMove = false }: {
    tree: Tree | null;
    fenBefore: string;
    fenAfter: string;
    uci: string;
    ownMoves: number;
    maxOwnMoves?: number;
    gameOver?: boolean;
    /** The move just played was the repertoire's move from that position. */
    repertoireMove?: boolean;
}): NextStep {
    if (gameOver) return { next: "end", reason: END_REASON.GAME_OVER };
    if (reachedMoveLimit({ ownMoves, maxOwnMoves, repertoireMove })) return { next: "end", reason: END_REASON.MAX_MOVES };

    const parent = lookup(tree, fenBefore);
    const played = findMove(parent, uci);
    const child = lookup(tree, fenAfter);

    if (child && sparringReplies(child).length) return { next: "reply" };
    // The repertoire's own move goes on past the line's book: the partner answers from the live explorer or the
    // repertoire (legacy/practice.js:playSparringMove), and the study of the opening carries on
    if (repertoireMove) return { next: "reply" };
    // Inside the tree but nothing deeper was generated: the line's book ends here.
    if (child || (played && shareOf(parent, uci) >= RARE_MOVE_SHARE)) {
        return { next: "end", reason: END_REASON.LINE_COMPLETE };
    }
    return { next: "offbook", rarity: played ? "rare" : "unknown" };
}

/**
 * Whether a catalog line answers from its own pre-generated tree. Only for whoever cannot have better: a visitor, or
 * an account without Lichess. With Lichess linked every line is answered live by the explorer, at the level of
 * /settings, as far as real games go (decided 2026-11-01: the catalog's trees end early in many branches).
 */
export function usesCatalogBook({ source, loggedIn, lichessConnected }: {
    source: string;
    loggedIn: boolean;
    lichessConnected: boolean;
}): boolean {
    return source === "catalog" && !(loggedIn && lichessConnected);
}

/** A move of the live explorer, as far as counting its games goes. */
interface CountedMove {
    white: number;
    draws: number;
    black: number;
}

/** What makes a position of the live explorer a book: enough games in it, and replies played more than once. */
export const MIN_LIVE_POSITION_GAMES = 10;
export const MIN_LIVE_MOVE_GAMES = 2;

/**
 * The replies the partner may play from the live explorer. It used to ask twenty games of each reply, and a position
 * of sixty games spread over six replies (19, 15, 12…) stopped the practice as if nobody had ever played on from
 * it (2026-11-01). A position needs ten games, and a reply two: one game is somebody's afternoon, not a book.
 */
export function liveReplies<T extends CountedMove>(moves: T[]): T[] {
    const games = (m: CountedMove) => m.white + m.draws + m.black;
    const total = moves.reduce((sum, m) => sum + games(m), 0);
    if (total < MIN_LIVE_POSITION_GAMES) return [];
    return moves.filter((m) => games(m) >= MIN_LIVE_MOVE_GAMES);
}

/** Whether the practice has had its moves: the limit, unless the user is still playing their repertoire. */
export function reachedMoveLimit({ ownMoves, maxOwnMoves = DEFAULT_MAX_OWN_MOVES, repertoireMove = false }: {
    ownMoves: number;
    maxOwnMoves?: number;
    repertoireMove?: boolean;
}): boolean {
    if (ownMoves >= REPERTOIRE_MAX_OWN_MOVES) return true;
    return !repertoireMove && ownMoves >= maxOwnMoves;
}

/** A practice counts (counter, login wall, streak) once it had a minimum of own moves. */
export function countsAsPractice({ ownMoves, endReason }: { ownMoves: number; endReason: EndReason }): boolean {
    return endReason !== END_REASON.ABANDONED && ownMoves >= MIN_MOVES_TO_COUNT;
}

/** True when an anonymous visitor has used up the free practices and must log in to start another. */
export function isWallReached({ isLoggedIn, completedCount, limit }: {
    isLoggedIn: boolean;
    completedCount: number;
    limit: number;
}): boolean {
    return !isLoggedIn && limit > 0 && completedCount >= limit;
}

export function endTitle(reason: EndReason): string {
    // Abandoned has no title of its own: nothing is shown for a practice the user walked away from.
    const titles: Partial<Record<EndReason, string>> = {
        [END_REASON.LINE_COMPLETE]: "End of the book",
        [END_REASON.MAX_MOVES]: "Practice complete",
        [END_REASON.OFF_BOOK]: "You left the book",
        [END_REASON.GAME_OVER]: "Game over",
    };
    return titles[reason] || "Practice finished";
}
